import type { WatchdogRow, WatchdogStatus } from './watchdog-types';
import { loadWatchdogData } from './watchdog-data';
import { renderWatchdogResults, showWatchdogState, updateLastUpdatedLabel, hideRowMenu } from './watchdog-results';

// ── Module state ──────────────────────────────────────────────────────────

export let watchdogRows: WatchdogRow[] = [];
export let wdSortCol = 'lastComm';           // sorts by offline age; ascending = OK devices first (matches reference)
export let wdSortAsc = true;
export let wdSearch = '';
export let wdStatusFilter: 'all' | WatchdogStatus = 'all';
export let wdGroupFilter = 'all';
export let wdModelFilter = 'all';
export let wdPage = 0;
export let wdPageSize = 10;
export let wdLastUpdated: Date | null = null;

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let displayTimer: ReturnType<typeof setInterval> | null = null;
let loadInFlight = false;

const REFRESH_MS = 30_000;

export function setWdSortCol(c: string):                     void { wdSortCol = c; }
export function setWdSortAsc(a: boolean):                    void { wdSortAsc = a; }
export function setWdSearch(s: string):                      void { wdSearch = s; }
export function setWdStatusFilter(s: 'all' | WatchdogStatus): void { wdStatusFilter = s; }
export function setWdGroupFilter(g: string):                 void { wdGroupFilter = g; }
export function setWdModelFilter(m: string):                 void { wdModelFilter = m; }
export function setWdPage(p: number):                        void { wdPage = p; }
export function setWdPageSize(n: number):                    void { wdPageSize = n; }

// ── Formatters ────────────────────────────────────────────────────────────

const MIN = 60_000, HOUR = 60 * MIN, DAY = 24 * HOUR;

/** "2h 14m" / "1d 2h 19m" / "6h 7m" — elapsed time since last communication. */
export function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return '—';
  const days  = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const mins  = Math.floor((ms % HOUR) / MIN);
  if (days > 0)  return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/** "Today, 10:24 AM" / "Yesterday, 04:12 PM" / "May 25, 2025, 11:47 AM". Older dates always carry
 *  the year — without it, a device offline since last year reads as if it just happened this
 *  month, which is exactly the kind of stale-data confusion this page exists to prevent. */
export function formatLastComm(date: Date | null): string {
  if (!date) return 'Never';
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, now))       return `Today, ${time}`;
  if (sameDay(date, yesterday)) return `Yesterday, ${time}`;
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}, ${time}`;
}

// ── Data refresh ────────────────────────────────────────────────────────────

/**
 * Load the connectivity snapshot. Overlap-guarded so a slow fetch can't stack behind the
 * 30s timer (same defence as dashboard.ts:49). `silent` skips the loading state so periodic
 * refreshes don't flash the spinner.
 */
export async function refreshWatchdog(silent = false): Promise<void> {
  if (loadInFlight) return;
  loadInFlight = true;
  if (!silent && !watchdogRows.length) showWatchdogState('loading', 'Checking device connectivity…');

  try {
    watchdogRows = await loadWatchdogData();
    wdLastUpdated = new Date();
    if (!watchdogRows.length) {
      showWatchdogState('empty');
    } else {
      renderWatchdogResults();
      showWatchdogState('results');
    }
    const exportBtn = document.getElementById('wdExportBtn') as HTMLElement | null;
    if (exportBtn) exportBtn.style.display = watchdogRows.length ? '' : 'none';
  } catch (err) {
    if (!watchdogRows.length) showWatchdogState('error', 'Error: ' + (err as Error).message);
  } finally {
    loadInFlight = false;
  }
}

/** Re-render from cached rows on filter/sort/page change (no re-fetch). */
export function wdRecalculate(): void {
  if (!watchdogRows.length) return;
  renderWatchdogResults();
}

// ── Auto-refresh loop (runs only while the page is visible) ──────────────────

export function startWatchdogRefresh(): void {
  if (refreshTimer) return;                       // already running — don't stack
  refreshTimer = setInterval(() => { void refreshWatchdog(true); }, REFRESH_MS);
  displayTimer = setInterval(updateLastUpdatedLabel, 10_000);
}

export function stopWatchdogRefresh(): void {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
  if (displayTimer) { clearInterval(displayTimer); displayTimer = null; }
}

// ── Page init ────────────────────────────────────────────────────────────────

export function initWatchdogPage(): void {
  // Kick off an immediate load every time the page is opened so data is fresh on entry.
  void refreshWatchdog(watchdogRows.length > 0);

  const root = document.getElementById('watchdogPage') as (HTMLElement & { _wired?: boolean }) | null;
  if (!root || root._wired) return;
  root._wired = true;

  document.getElementById('wdRefreshBtn')?.addEventListener('click', () => { void refreshWatchdog(false); });

  document.getElementById('wdExportBtn')?.addEventListener('click', async () => {
    const { exportWatchdogXlsx } = await import('./watchdog-export');
    void exportWatchdogXlsx();
  });

  const search = document.getElementById('wdSearch') as HTMLInputElement | null;
  search?.addEventListener('input', () => { setWdSearch(search.value); setWdPage(0); wdRecalculate(); });

  document.getElementById('wdStatusFilter')?.addEventListener('change', (e) => {
    setWdStatusFilter((e.target as HTMLSelectElement).value as 'all' | WatchdogStatus);
    setWdPage(0); wdRecalculate();
  });
  document.getElementById('wdGroupFilter')?.addEventListener('change', (e) => {
    setWdGroupFilter((e.target as HTMLSelectElement).value); setWdPage(0); wdRecalculate();
  });
  document.getElementById('wdModelFilter')?.addEventListener('change', (e) => {
    setWdModelFilter((e.target as HTMLSelectElement).value); setWdPage(0); wdRecalculate();
  });

  const pageSize = document.getElementById('wdPageSize') as HTMLSelectElement | null;
  pageSize?.addEventListener('change', () => {
    // "All" is value="0" — store it as-is (not a snapshot of the current row count) so the
    // dynamic fallback in renderTable() (`wdPageSize > 0 ? wdPageSize : sorted.length`) keeps
    // recomputing "all" fresh on every render, even after the device count changes later.
    setWdPageSize(parseInt(pageSize.value));
    setWdPage(0); wdRecalculate();
  });

  document.getElementById('wdPagination')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-page]') as HTMLElement | null;
    if (btn && !btn.hasAttribute('disabled')) { setWdPage(parseInt(btn.dataset.page!)); wdRecalculate(); }
  });

  document.getElementById('wdClearFilters')?.addEventListener('click', () => {
    setWdSearch(''); setWdStatusFilter('all'); setWdGroupFilter('all'); setWdModelFilter('all'); setWdPage(0);
    if (search) search.value = '';
    ['wdStatusFilter', 'wdGroupFilter', 'wdModelFilter'].forEach((id) => {
      const sel = document.getElementById(id) as HTMLSelectElement | null;
      if (sel) sel.value = 'all';
    });
    wdRecalculate();
  });

  // Troubleshoot modal close handlers (offline rows open it via window.__wdTroubleshoot).
  const modal = document.getElementById('wdModal');
  document.getElementById('wdModalClose')?.addEventListener('click', () => modal?.classList.remove('show'));
  modal?.addEventListener('click', (e) => { if ((e.target as Element).id === 'wdModal') modal.classList.remove('show'); });

  // Close the row (⋮) action menu on any outside click. Wired once for the page's lifetime.
  document.addEventListener('click', (e) => {
    const t = e.target as Element;
    if (!t.closest('.wd-menu-btn') && !t.closest('#wdRowMenu')) hideRowMenu();
  });
}

export function resetWatchdogPage(): void {
  stopWatchdogRefresh();
  watchdogRows    = [];
  wdSearch        = '';
  wdStatusFilter  = 'all';
  wdGroupFilter   = 'all';
  wdModelFilter   = 'all';
  wdPage          = 0;
  wdLastUpdated   = null;

  document.getElementById('wdEmpty')?.classList.remove('hidden');
  document.getElementById('wdResults')?.classList.add('hidden');
  document.getElementById('wdErrBox')?.classList.add('hidden');
  document.getElementById('wdModal')?.classList.remove('show');

  const exportBtn = document.getElementById('wdExportBtn') as HTMLElement | null;
  if (exportBtn) exportBtn.style.display = 'none';

  // NOTE: the page's event listeners are intentionally left wired. The dashboard DOM is static
  // across logout/login, so re-wiring would stack duplicate listeners (the exact bug the
  // dashboard "wire-once" guard exists to prevent). initWatchdogPage's _wired guard keeps
  // wiring to a single pass for the page's lifetime.

  const search = document.getElementById('wdSearch') as HTMLInputElement | null;
  if (search) search.value = '';
}
